'use client'

import React, {useRef, useState, useMemo, useEffect} from "react";
import {cn} from "@/lib/utils";
import Image, {StaticImageData} from "next/image";
import {HLSVideoPlayer} from "@/components/ui/SanityVideoPlayer";

export default function BackgroundVideo({
  playbackId,
  fallbackImage,
  className = '',
}: {
  playbackId: string;
  fallbackImage: StaticImageData;
  className?: string;
}){
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const videoSource = useMemo(() => ({
    embed: false,
    muxVideo: {
      playbackId: playbackId,
      assetId: "",
      filename: "",
      status: ""
    },
    embedUrl: "",
    thumbnail: null
  }), [playbackId]);

  const handleError = () => {
    setHasError(true);
  };

  const playVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playVideo();
          } else {
            pauseVideo();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.01,
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute inset-0 w-full h-full",
        className
      )}
    >
      <Image
        className={cn(
          "absolute !w-full !h-full object-cover transition-opacity duration-150",
          isPlaying && !hasError ? "opacity-0" : "opacity-100"
        )}
        src={fallbackImage}
        alt=""
      />

      {!hasError && (
        <HLSVideoPlayer
          ref={videoRef}
          source={videoSource}
          autoPlay={false}
          muted={true}
          loop={true}
          playsInline
          className={cn(
            "!w-full !h-full object-cover transition-opacity duration-150",
            !isPlaying && "opacity-0"
          )}
          onError={handleError}
        />
      )}
    </div>
  );
}